import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { equipCharacterModule, unequipCharacterModule, completeCharacter } from '../store/characterSlice';
import { fetchModules } from '../store/inventorySlice';
import { PART_LAYOUT } from './CharacterAssemblyPreview';
import InventoryPickerModal from './InventoryPickerModal';
import RankBadge from './RankBadge';
import StatChips from '../../../shared/components/StatChips';
import { perkText } from '../../../shared/utils/heroStats';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import '../styles/character.css';
import '../styles/merge.css';
import { useTranslation } from 'react-i18next';
import { moduleSlotLabel, partName } from '../../../shared/utils/gameLabels';

// Paper-doll layout: left column = head / left hand / left leg, right column = body / right hand / right leg
const SLOTS = [
  { key: 'headModule',      type: 'HEAD',       label: 'Head',    side: 'left' },
  { key: 'bodyModule',      type: 'BODY',       label: 'Body',    side: 'right' },
  { key: 'leftHandModule',  type: 'LEFT_HAND',  label: 'L. Hand', side: 'left' },
  { key: 'rightHandModule', type: 'RIGHT_HAND', label: 'R. Hand', side: 'right' },
  { key: 'leftLegModule',   type: 'LEFT_LEG',   label: 'L. Leg',  side: 'left' },
  { key: 'rightLegModule',  type: 'RIGHT_LEG',  label: 'R. Leg',  side: 'right' },
];

function BuilderAssemblyPreview({ character, complete, highlightKey }) {
  const parts = PART_LAYOUT.map(({ key, left, top, size, z }) => {
    const mod = character?.[key];
    if (!mod) return null;
    return (
      <img
        key={key}
        className={`builder-assembly-part ${highlightKey === key ? 'is-highlighted' : ''}`}
        src={resolveAssetUrl(mod.imageUrl)}
        alt={mod.name || key}
        style={{ left, top, width: size, zIndex: z }}
      />
    );
  });

  if (complete) {
    return (
      <div className="builder-complete-visual">
        <img className="builder-complete-shine" src="/character_builder/shine.png" alt="" />
        <div className="builder-assembly-wrap builder-assembly-wrap--large">
          <img className="builder-assembly-silhouette" src="/inventory/character.png" alt="character" />
          {parts}
        </div>
        <img className="builder-complete-floor" src="/character_builder/floor.png" alt="" />
      </div>
    );
  }

  return (
    <div className="builder-assembly-wrap builder-assembly-wrap--doll">
      <img className="builder-assembly-silhouette" src="/inventory/character.png" alt="character" />
      {parts}
    </div>
  );
}

/** Hero STR/AGI/INT + summed part perks */
function HeroStatsPanel({ character }) {
  const perks = (character.perks || []).map((p) => perkText(p.type, p.value)).filter(Boolean);
  return (
    <div className="character-builder-stats">
      <StatChips str={character.str} agi={character.agi} int={character.int} power={character.power} size="md" />
      {perks.length > 0 && (
        <div className="character-builder-perks">
          {perks.map((p) => <span key={p} className="perk-line">{p}</span>)}
        </div>
      )}
    </div>
  );
}

function SlotCell({ slot, module, onClick, disabled, active }) {
  const { t } = useTranslation();
  const label = moduleSlotLabel(t, slot.type);
  return (
    <button
      type="button"
      className={`character-builder-slot ${module ? 'filled' : 'empty'} ${module?.rank > 0 ? 'ranked' : ''} ${active ? 'active' : ''}`}
      style={{ '--rank-color': module?.rankColor }}
      onClick={onClick}
      disabled={disabled}
      title={module
        ? t('builder.slot_replace', '{{name}} — tap to replace', { name: partName(t, module) })
        : t('builder.slot_add', 'Add {{part}}', { part: label })}
    >
      {module ? (
        <>
          <RankBadge rank={module.rank || 0} color={module.rankColor} name={module.rankName} />
          <img src={resolveAssetUrl(module.imageUrl)} alt={partName(t, module)} />
          <span className="character-builder-slot-power">⚡{module.power ?? 0}</span>
        </>
      ) : (
        <span className="character-builder-slot-plus">+</span>
      )}
      <span className="character-builder-slot-label">{label}</span>
    </button>
  );
}

export default function CharacterBuilderModal({ character, allModules, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isEquipping, error } = useSelector((s) => s.characters);
  const [activeSlot, setActiveSlot] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(false);

  const latest = useSelector((s) =>
    s.characters.list.find((c) => c.id === character.id)
  ) || character;

  const filledCount = SLOTS.filter((s) => latest[s.key] !== null && latest[s.key] !== undefined).length;
  const allFilled = filledCount === SLOTS.length;

  // Parts move between the hero and the inventory → keep the inventory list fresh
  const refreshInventory = () => dispatch(fetchModules(null));

  const handleSlotClick = (slot) => {
    if (isEquipping) return;
    setConfirmComplete(false);
    setActiveSlot(slot);
  };

  const handleSelectModule = async (inventoryItem) => {
    if (!activeSlot) return;
    setActiveSlot(null);
    await dispatch(equipCharacterModule({
      characterId: latest.id,
      moduleId: inventoryItem.module.id,
      rank: inventoryItem.rank || 0,
    }));
    refreshInventory();
  };

  const handleRemove = async () => {
    if (!activeSlot) return;
    const slotType = activeSlot.type;
    setActiveSlot(null);
    await dispatch(unequipCharacterModule({ characterId: latest.id, slotType }));
    refreshInventory();
  };

  const handleComplete = () => {
    if (!allFilled || isEquipping) return;
    if (!confirmComplete) {
      setConfirmComplete(true);
      return;
    }
    dispatch(completeCharacter(latest.id));
  };

  if (latest.complete) {
    return (
      <div className="character-builder-overlay" onClick={onClose}>
        <div className="character-builder-modal" onClick={(e) => e.stopPropagation()}>
          <div className="character-builder-title character-builder-title--complete">
            {t('builder.complete_title', 'Character is complete!')}
          </div>

          <BuilderAssemblyPreview character={latest} complete />

          {latest.uniformSet && (
            <div className="character-builder-power character-builder-power--big">
              <span className="character-builder-set">{t('builder.full_set', 'Full set bonus')}</span>
            </div>
          )}

          <HeroStatsPanel character={latest} />

          <div className="character-builder-slots character-builder-slots--complete">
            {SLOTS.map((slot) => {
              const module = latest[slot.key];
              return (
                <div
                  key={slot.key}
                  className={`character-builder-slot filled ${module?.rank > 0 ? 'ranked' : ''}`}
                  style={{ '--rank-color': module?.rankColor }}
                >
                  {module && (
                    <>
                      <RankBadge rank={module.rank || 0} color={module.rankColor} name={module.rankName} />
                      <img src={resolveAssetUrl(module.imageUrl)} alt={partName(t, module)} />
                      <span className="character-builder-slot-label">{moduleSlotLabel(t, slot.type)}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="app-btn app-btn--green character-builder-cta"
            onClick={() => navigate('/lair', { state: { tab: 'Missions' } })}
          >
            {latest.onMission ? t('builder.on_mission_view', 'On a mission — view') : t('builder.send_on_mission', 'Send on a mission')}
          </button>
          <button className="character-builder-close" onClick={onClose}>
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    );
  }

  const left = SLOTS.filter((s) => s.side === 'left');
  const right = SLOTS.filter((s) => s.side === 'right');

  return (
    <div className="character-builder-overlay" onClick={onClose}>
      <div className="character-builder-modal" onClick={(e) => e.stopPropagation()}>

        <div className="character-builder-title">{t('builder.title', 'Character creation')}</div>
        <div className="character-builder-progress">
          <span>{filledCount}</span>/{SLOTS.length}
        </div>

        <HeroStatsPanel character={latest} />

        <div className="character-builder-doll">
          <div className="character-builder-col">
            {left.map((slot) => (
              <SlotCell key={slot.key} slot={slot} module={latest[slot.key]}
                        active={activeSlot?.key === slot.key}
                        disabled={isEquipping} onClick={() => handleSlotClick(slot)} />
            ))}
          </div>
          <BuilderAssemblyPreview character={latest} highlightKey={activeSlot?.key} />
          <div className="character-builder-col">
            {right.map((slot) => (
              <SlotCell key={slot.key} slot={slot} module={latest[slot.key]}
                        active={activeSlot?.key === slot.key}
                        disabled={isEquipping} onClick={() => handleSlotClick(slot)} />
            ))}
          </div>
        </div>

        <div className="character-builder-hint">
          {allFilled
            ? (confirmComplete
              ? t('builder.hint_confirm', 'Parts will be locked forever. Tap again to confirm.')
              : t('builder.hint_all_filled', 'All parts in place. You can still swap them — complete when ready.'))
            : t('builder.hint', 'Tap a slot to add or swap a part. Each part adds stats and its own bonus; merged parts are stronger.')}
        </div>

        {error && (
          <div style={{ color: '#ff6b6b', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>
            {error}
          </div>
        )}

        {allFilled && (
          <button
            type="button"
            className={`app-btn ${confirmComplete ? 'app-btn--yellow' : 'app-btn--green'} character-builder-cta`}
            onClick={handleComplete}
            disabled={isEquipping}
          >
            {confirmComplete ? t('builder.confirm_complete', 'Confirm & complete') : t('builder.complete', 'Complete character')}
          </button>
        )}

        <button className="character-builder-close" onClick={onClose}>
          {t('common.close', 'Close')}
        </button>
      </div>

      {activeSlot && (
        <InventoryPickerModal
          slotType={activeSlot.type}
          slotLabel={activeSlot.label}
          modules={allModules}
          current={latest[activeSlot.key]}
          onSelect={handleSelectModule}
          onRemove={handleRemove}
          onClose={() => setActiveSlot(null)}
        />
      )}
    </div>
  );
}
